/** @format */

import React, { useEffect, useState } from "react";
import {
  Link,
  useLocation,
  useSearchParams,
  useNavigate,
  Outlet,
  useRouteError,
  isRouteErrorResponse,
  useParams,
} from "react-router-dom";
import "./App.scss";
import { ReactComponent as WJECLogo } from "./wjec.svg";
import { ReactComponent as CBACLogo } from "./cbac.svg";
import { ReactComponent as EduqasLogo } from "./eduqas.svg";
import { ReactComponent as WindowPlus } from "bootstrap-icons/icons/window-plus.svg";
import { ReactComponent as Background } from "./header_shapes.svg";
import { ReactComponent as InfoCircle } from "bootstrap-icons/icons/info-circle.svg";
import LocaleSwitch from "./i18n/LocaleSwitch";
import { Trans, useTranslation } from "react-i18next";
import { useLocale } from "./i18n/hooks";
import {
  selectBrand,
  setSubjects,
  Subject,
} from "./features/resources/resourcesSlice";
import { useAnalytics } from "use-analytics";
import { useDispatch, useSelector } from "react-redux";
import UserWidget from "./features/user/UserWidget";
import { ErrorResponse } from "@remix-run/router";
import { setUserToken, UserToken } from "./features/auth/authSlice";
import { Buffer } from "buffer";
import { Button } from "react-bootstrap";
import { useAppSelector } from "./app/hooks";

import TermsConditions from "./features/user/termsConditions/TermsConditions";
import AuthFlag from "./features/auth/AuthFlag";
import Notifications from "./features/notifications/Notifications";

function App(): JSX.Element {
  const [Logo, setLogo] = useState<React.FC<{ className?: string }>>(WJECLogo);
  const currentLocale = useLocale();
  const { t: _ } = useTranslation();
  const { page } = useAnalytics();
  const location = useLocation();
  const dispatch = useDispatch();
  const brand = useSelector(selectBrand());
  const [searchParams, setSearchParams] = useSearchParams();
  const { subject, resourceId } = useParams();
  const error = useRouteError() as ErrorResponse | undefined;
  const isAuthenticated =
    useAppSelector((state) => state.auth.userToken?.token) || false;
  const navigate = useNavigate();

  /** Fix for eduqas state / querystring / domain */

  useEffect(() => {
    const tkid = searchParams.get("tkid");
    if (!!tkid) {
      const tokenKeys = ["expires", "refreshToken", "token", "service"];
      const token: UserToken = JSON.parse(
        Buffer.from(tkid, "base64").toString("utf-8"),
      );

      if (
        Object.keys(token).every((key) => tokenKeys.includes(key)) &&
        token.expires > Date.now()
      ) {
        dispatch(setUserToken(token)); // Update user token with new details
      }

      searchParams.delete("tkid");
      setSearchParams(searchParams, { replace: true });
    }
  }, [dispatch, searchParams, setSearchParams]);

  useEffect(() => {
    const hasEduqasParam = searchParams.get("eduqas") !== null;
    const isEduqasHostname = document.location.hostname.includes("eduqas");

    if (hasEduqasParam || isEduqasHostname) {
      window.location.href = "https://resources.eduqas.co.uk"; // Navigate to eduqas site if required

      //dispatch(setBrand("eduqas"));
    } else if (brand === "eduqas" && !isEduqasHostname) {
      const updatedSearchParams = new URLSearchParams(searchParams);
      updatedSearchParams.set("eduqas", "");
      setSearchParams(updatedSearchParams, { replace: true });
    }
  }, [searchParams, navigate, brand, setSearchParams]);

  useEffect(() => {
    if (!brand) return;
    import(`./json/${brand}Subjects.json`).then(({ default: _subjects }) => {
      dispatch(setSubjects(_subjects[currentLocale] as Subject[]));
    });
  });

  useEffect(() => {
    setLogo(
      currentLocale === "cy"
        ? CBACLogo
        : brand === "eduqas"
        ? EduqasLogo
        : WJECLogo,
    );
  }, [currentLocale, brand]);

  useEffect(() => {
    page();
  }, [page, location]);

  return (
    <div className={`App ${brand}`}>
      <header>
        <div className="navbar navbar-expand-lg navbar-light py-1 pb-0 border-bottom">
          <div className="container-fluid">
            <Link className="navbar-brand z-3 fs-3" to="/">
              {_("Resource filter")}
            </Link>
            <div className="ms-auto navbar-right-content">
              <AuthFlag authGroup={["wjec", "admin"]}>
                <Button
                  variant="outline-editing"
                  className="z-3 create-resource-btn me-1 me-sm-3 d-flex align-items-center bg-white"
                  onClick={() => navigate("/select-resource")}
                >
                  <WindowPlus className="me-sm-2 h-75 w-auto mx-auto" />
                  <span className="d-none d-sm-inline-block">
                    {_("Create resource")}
                  </span>
                </Button>
              </AuthFlag>
              {brand !== "eduqas" && (
                <LocaleSwitch className="z-3 d-none d-sm-block" />
              )}

              <div className="z-3 me-1 me-sm-3 d-inline-block">
                <UserWidget />
              </div>

              <a
                className="d-inline-block"
                href={`https://www.${
                  brand === "eduqas" ? "eduqas.co.uk" : _("wjec.co.uk")
                }/`}
                rel="noreferrer"
                target="_blank"
              >
                <Logo className="wjec-logo" />
              </a>
            </div>
          </div>

          <Background className="headerShapes d-none d-sm-inline-block" />
        </div>
        {subject !== undefined && resourceId === undefined && (
          <div
            className="user-resource-info banner-info py-2 px-2 px-sm-3 "
            style={{ minHeight: "calc(2rem)" }}
          >
            <div className="container-fluid d-flex flex-row align-items-center gap-2 ps-0">
              <InfoCircle className="flex-shrink-0" />
              <p className="m-0">
                <small>
                  <Trans
                    i18nKey="legacy-resource-disclaimer"
                    // eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/anchor-has-content
                    components={{ p: <p />, a: <a /> }}
                    values={{
                      urlLink: `${
                        currentLocale === "en"
                          ? "<a href='https://resources-legacy.wjec.co.uk' target='_blank'>our previous resources site</a>"
                          : "<a href='https://adnoddau-hen.cbac.co.uk' target='_blank'>ein gwefan adnoddau blaenorol</a>"
                      }`,
                    }}
                  />
                </small>
              </p>
            </div>
          </div>
        )}

        {brand !== "eduqas" && (
          <div className="container-fluid d-flex fw-bold">
            <LocaleSwitch className="d-sm-none py-2 locale-switch ms-auto" />
          </div>
        )}
      </header>

      <main className="flex-grow-1">
        <div className="content container-fluid pt-0 pt-sm-3 pb-0 h-100">
          {isRouteErrorResponse(error) && (
            <h1>
              {error.status} {error.statusText}
            </h1>
          )}
          <Outlet />
        </div>
      </main>

      <Notifications />

      <footer>
        <div className="bg-dark border-top py-2 text-start text-dark">
          <div className="container-fluid">
            <small>
              &copy; {_("copyright")}&ndash;{new Date().getFullYear()}
            </small>
          </div>
        </div>
      </footer>

      {isAuthenticated && <TermsConditions />}
    </div>
  );
}

export default App;
